import React, { useMemo } from 'react';
import { Spinner } from 'react-bootstrap';
import { useMeterAlerts } from '../../hooks/useMeters';
import { buildTimeline } from '../../utils/meterAlerts';
import { readingStateLabel } from '../../utils/meterFilters';
import '../../styles/DowntimeAlerts.css';

/**
 * When this meter stopped reporting, and for how long.
 *
 * The alerts are already raised and stored against the meter, but nothing
 * surfaced them — so "why is this month low?" had no answer on the page that
 * prompts the question.
 *
 * @param {string} meterId - The meter whose history to show.
 */
const DowntimeAlerts = ({ meterId }) => {
  const { data, isLoading, error } = useMeterAlerts(meterId);
  const { rows, openCount } = useMemo(() => buildTimeline(data), [data]);

  if (isLoading) {
    return (
      <div className="im-dt im-dt--quiet">
        <Spinner animation="border" size="sm" variant="primary" />
      </div>
    );
  }

  if (error) {
    return <div className="im-dt im-dt--quiet">Alert history could not be loaded.</div>;
  }

  if (!rows.length) {
    return (
      <div className="im-dt im-dt--quiet">
        No alert has ever been raised against this meter.
      </div>
    );
  }

  return (
    <div className="im-dt">
      {openCount > 0 && (
        <p className="im-dt-open">
          {openCount} {openCount === 1 ? 'alert is' : 'alerts are'} still open.
        </p>
      )}

      {rows.map((row) => (
        <div className="im-dt-row" key={row.id}>
          <span className="im-dt-when">{row.when}</span>

          <span className="im-dt-what">
            {row.settled ? (
              <>Reporting normally for <b>{row.length}</b></>
            ) : (
              <>
                <b>Meter inactivity</b> — no data for {row.length}
                {row.readingState ? `, ${readingStateLabel(row.readingState).toLowerCase()}` : ''}
                {row.resolvedAt ? `, resolved ${row.resolvedAt}` : ''}
              </>
            )}
          </span>

          <span className={`im-dt-tag im-dt-tag--${row.tone}`}>{row.label}</span>
        </div>
      ))}
    </div>
  );
};

export default DowntimeAlerts;
